/**
 * Run-history viewer for one scheduled job. Mounted from
 * `<ScheduledJobsSection>` when the user clicks 「履歴を見る」.
 *
 * Two views compose vertically:
 *   1. A ~30-day success-rate sparkline rendered with raw inline SVG —
 *      a heavier chart lib would balloon the bundle for a single panel.
 *      Each cell is a day-bucket coloured by the worst status of that
 *      day's runs (red for any failure, yellow for partial, green for
 *      all success, slate for "no runs that day").
 *   2. A scrollable table of recent runs (newest first), with an
 *      expand-on-click row that reveals the stack trace when the run
 *      failed. Empty state covers freshly-seeded jobs that haven't
 *      ticked yet.
 */
import { useMemo, useState } from 'react';
import { X, ChevronDown, ChevronRight } from 'lucide-react';
import type { ScheduledJobRunDoc } from '@mct/shared';
import { Spinner } from '../ui/Spinner';
import { Badge } from '../ui/Badge';
import { useScheduledJobRuns } from '../../hooks/useScheduledJobs';

type DayBucket = {
  iso: string; // YYYY-MM-DD (UTC)
  total: number;
  success: number;
  failed: number;
  partial: number;
  worst: 'none' | 'success' | 'partial' | 'failed';
};

function bucketByDay(runs: ScheduledJobRunDoc[], days = 30): DayBucket[] {
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const buckets: DayBucket[] = [];
  const byIso = new Map<string, DayBucket>();
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(today.getTime() - i * 86_400_000);
    const iso = d.toISOString().slice(0, 10);
    const b: DayBucket = {
      iso,
      total: 0,
      success: 0,
      failed: 0,
      partial: 0,
      worst: 'none',
    };
    buckets.push(b);
    byIso.set(iso, b);
  }
  for (const r of runs) {
    if (r.status === 'running') continue;
    const startedAt = r.startedAt?.toDate?.();
    if (!startedAt) continue;
    const iso = startedAt.toISOString().slice(0, 10);
    const b = byIso.get(iso);
    if (!b) continue;
    b.total += 1;
    if (r.status === 'failed') b.failed += 1;
    else if (r.status === 'partial') b.partial += 1;
    else b.success += 1;
    // "worst-of-day" wins: failed > partial > success.
    if (b.failed > 0) b.worst = 'failed';
    else if (b.partial > 0) b.worst = 'partial';
    else if (b.success > 0) b.worst = 'success';
  }
  return buckets;
}

function bucketColor(b: DayBucket): string {
  switch (b.worst) {
    case 'failed':
      return '#fca5a5'; // red-300
    case 'partial':
      return '#fde68a'; // yellow-300
    case 'success':
      return '#86efac'; // green-300
    default:
      return '#e2e8f0'; // slate-200
  }
}

function statusBadge(s: ScheduledJobRunDoc['status']) {
  if (s === 'success') return <Badge tone="green">成功</Badge>;
  if (s === 'failed') return <Badge tone="red">失敗</Badge>;
  if (s === 'partial') return <Badge tone="yellow">部分成功</Badge>;
  return <Badge tone="slate">実行中</Badge>;
}

function formatTime(ts: { toDate(): Date } | null | undefined): string {
  if (!ts) return '—';
  const d = ts.toDate();
  return d.toLocaleString('ja-JP');
}

export function ScheduledJobHistory({
  projectId,
  jobId,
  onClose,
}: {
  projectId: string;
  jobId: string;
  onClose: () => void;
}) {
  const { data: runs, loading } = useScheduledJobRuns(projectId, jobId, 100);
  const [expanded, setExpanded] = useState<string | null>(null);

  const dayBuckets = useMemo(() => bucketByDay(runs, 30), [runs]);

  const aggregate = useMemo(() => {
    const finished = runs.filter((r) => r.status !== 'running');
    const ok = finished.filter((r) => r.status === 'success').length;
    const total = finished.length;
    return { total, ok, rate: total === 0 ? null : Math.round((ok / total) * 100) };
  }, [runs]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4 py-6"
      role="dialog"
      data-testid="scheduled-job-history-modal"
    >
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h3 className="text-base font-semibold">
            実行履歴 — <span className="font-mono">{jobId}</span>
          </h3>
          <button
            type="button"
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="閉じる"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-auto px-4 py-3">
          <section>
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">直近 30 日の成功率</h4>
              <span className="text-xs text-slate-500">
                {aggregate.total === 0
                  ? '実行履歴なし'
                  : `成功率 ${aggregate.rate}% (${aggregate.ok}/${aggregate.total})`}
              </span>
            </div>
            <svg
              className="mt-2 h-8 w-full"
              viewBox={`0 0 ${dayBuckets.length * 12} 28`}
              preserveAspectRatio="none"
              data-testid="scheduled-job-history-chart"
            >
              {dayBuckets.map((b, i) => (
                <rect
                  key={b.iso}
                  x={i * 12}
                  y={4}
                  width={10}
                  height={20}
                  rx={2}
                  fill={bucketColor(b)}
                >
                  <title>
                    {b.iso}: {b.total} 件 (成功 {b.success} / 部分 {b.partial} / 失敗 {b.failed})
                  </title>
                </rect>
              ))}
            </svg>
          </section>

          <section className="mt-5">
            <h4 className="text-sm font-semibold">直近の実行</h4>
            {loading ? (
              <div className="mt-3 flex justify-center">
                <Spinner />
              </div>
            ) : runs.length === 0 ? (
              <p className="mt-2 text-xs text-slate-500">
                まだ実行履歴がありません。
              </p>
            ) : (
              <div className="mt-2 overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-50 text-left text-slate-600">
                    <tr>
                      <th className="px-2 py-1.5 font-medium">実行時刻</th>
                      <th className="px-2 py-1.5 font-medium">状態</th>
                      <th className="px-2 py-1.5 font-medium">所要</th>
                      <th className="px-2 py-1.5 font-medium">処理件数</th>
                      <th className="px-2 py-1.5 font-medium">トリガ</th>
                      <th className="px-2 py-1.5 font-medium">サマリ / エラー</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((r) => {
                      const isExpanded = expanded === r.runId;
                      return (
                        <RunRow
                          key={r.runId}
                          run={r}
                          isExpanded={isExpanded}
                          onToggle={() =>
                            setExpanded(isExpanded ? null : r.runId)
                          }
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function RunRow({
  run,
  isExpanded,
  onToggle,
}: {
  run: ScheduledJobRunDoc;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const hasDetails = !!run.errorStack;
  return (
    <>
      <tr className="border-t border-slate-100">
        <td className="px-2 py-1.5 tabular-nums">{formatTime(run.startedAt)}</td>
        <td className="px-2 py-1.5">{statusBadge(run.status)}</td>
        <td className="px-2 py-1.5 tabular-nums">
          {run.durationMs ?? '—'} ms
        </td>
        <td className="px-2 py-1.5 tabular-nums">
          {run.itemsProcessed ?? '—'}
        </td>
        <td className="px-2 py-1.5">
          {run.triggeredBy === 'manual' ? (
            <Badge tone="blue">manual</Badge>
          ) : (
            <Badge tone="slate">schedule</Badge>
          )}
        </td>
        <td className="px-2 py-1.5">
          <div className="flex items-center gap-1">
            <span className="truncate">
              {run.summary ?? run.errorMessage ?? '—'}
            </span>
            {hasDetails ? (
              <button
                type="button"
                className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                aria-label="詳細を表示"
                onClick={onToggle}
              >
                {isExpanded ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
              </button>
            ) : null}
          </div>
        </td>
      </tr>
      {isExpanded && hasDetails ? (
        <tr className="border-t border-slate-100">
          <td colSpan={6} className="bg-slate-50 px-2 py-2">
            <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-[11px] leading-snug text-slate-700">
              {run.errorStack}
            </pre>
          </td>
        </tr>
      ) : null}
    </>
  );
}
